<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import DeleteDialog from 'dashboard/components-next/aifocus/pageComponents/DeleteDialog.vue';

// Tab Components
import SettingsTab from './tabs/SettingsTab.vue';
import ToolsTab from './tabs/ToolsTab.vue';
import McpTab from './tabs/McpTab.vue';
import GuardrailsTab from './tabs/GuardrailsTab.vue';
import InboxesTab from './tabs/InboxesTab.vue';
import FollowUpTab from './tabs/FollowUpTab.vue';
import LeadsTab from './tabs/LeadsTab.vue';
import PlaygroundTab from './tabs/PlaygroundTab.vue';
import LogsTab from './tabs/LogsTab.vue';
import AtendimentoTab from './tabs/AtendimentoTab.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const deleteAgentDialog = ref(null);
const activeTabRef = ref(null);

// Role-based access
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

// Active tab state
const activeTab = ref(isSuperAdmin.value ? 'settings' : 'followup');

// Map tab IDs to components
const tabComponents = {
  settings: SettingsTab,
  atendimento: AtendimentoTab,
  playground: PlaygroundTab,
  inboxes: InboxesTab,
  followup: FollowUpTab,
  leads: LeadsTab,
  tools: ToolsTab,
  mcp: McpTab,
  guardrails: GuardrailsTab,
  logs: LogsTab,
};
const activeTabComponent = computed(() => tabComponents[activeTab.value]);

// All tab definitions
const allTabs = [
  {
    id: 'settings',
    label: 'Configuracoes',
    icon: 'i-lucide-settings',
    superOnly: true,
  },
  {
    id: 'atendimento',
    label: 'Atendimento',
    icon: 'i-lucide-headphones',
    superOnly: true,
  },
  {
    id: 'playground',
    label: 'Playground',
    icon: 'i-lucide-play',
    requiresSync: true,
    superOnly: true,
  },
  { id: 'inboxes', label: 'Inboxes', icon: 'i-lucide-inbox', superOnly: true },
  { id: 'followup', label: 'Follow-up', icon: 'i-lucide-repeat' },
  { id: 'leads', label: 'Produtos', icon: 'i-lucide-database' },
  { id: 'tools', label: 'Tools', icon: 'i-lucide-wrench', superOnly: true },
  { id: 'mcp', label: 'MCP', icon: 'i-lucide-plug', superOnly: true },
  {
    id: 'guardrails',
    label: 'Guardrails',
    icon: 'i-lucide-shield',
    superOnly: true,
  },
  { id: 'logs', label: 'Logs', icon: 'i-lucide-scroll-text', superOnly: true },
];

// Filtered tabs based on role
const tabs = computed(() => {
  if (isSuperAdmin.value) return allTabs;
  return allTabs.filter(tab => !tab.superOnly);
});

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const agentId = computed(() => {
  const id = Number(route.params.agentId);
  return Number.isNaN(id) ? null : id;
});
const agent = computed(() => {
  if (!agentId.value) return null;
  return store.getters['aifocusAgents/getRecord'](agentId.value);
});
const hasValidAgentId = computed(
  () => agentId.value !== null && agentId.value > 0
);

const isSynced = computed(() => !!agent.value?.external_agent_id);

// Fetch agent data on mount
onMounted(async () => {
  await store.dispatch('aifocusAgents/get');
});

const handleBack = () => {
  router.push({
    name: 'aifocus_agents_settings_index',
    params: { accountId: route.params.accountId },
  });
};

const handleDelete = () => {
  deleteAgentDialog.value.dialogRef.open();
};

const handleDeleteSuccess = () => {
  router.push({
    name: 'aifocus_agents_settings_index',
    params: { accountId: route.params.accountId },
  });
};

const setActiveTab = tabId => {
  const tab = tabs.value.find(t => t.id === tabId);
  // Check if tab requires sync
  if (tab?.requiresSync && !isSynced.value) {
    return;
  }
  activeTab.value = tabId;
};

const isTabDisabled = tab => {
  return tab.requiresSync && !isSynced.value;
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #header>
      <div class="flex flex-col w-full">
        <!-- Top Header -->
        <div class="flex items-center justify-between w-full px-6 py-4">
          <div class="flex items-center gap-4">
            <Button
              icon="i-lucide-arrow-left"
              variant="ghost"
              size="sm"
              @click="handleBack"
            />
            <div>
              <h1 class="text-xl font-semibold text-n-slate-12">
                {{ agent?.name || 'Editar Agente' }}
              </h1>
              <p class="text-sm text-n-slate-11">
                Configure as informacoes do agente
              </p>
            </div>
          </div>

          <!-- Sync Status Badge -->
          <div class="flex items-center gap-3">
            <span
              v-if="isSynced"
              class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-green-500/10 text-green-600"
            >
              <span class="w-2 h-2 rounded-full bg-green-500" />
              Sincronizado
            </span>
            <span
              v-else
              class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-yellow-500/10 text-yellow-600"
            >
              <span class="w-2 h-2 rounded-full bg-yellow-500" />
              Nao sincronizado
            </span>
          </div>
        </div>

        <!-- Tabs Navigation -->
        <div class="px-6 border-b border-n-weak overflow-x-auto">
          <nav class="flex gap-0 -mb-px min-w-max">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="flex items-center gap-1.5 px-2.5 py-3 text-xs font-medium transition-colors border-b-2 -mb-px whitespace-nowrap flex-shrink-0"
              :class="[
                activeTab === tab.id
                  ? 'border-n-brand text-n-brand'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-6',
                isTabDisabled(tab)
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer',
              ]"
              :disabled="isTabDisabled(tab)"
              :title="isTabDisabled(tab) ? 'Sincronize o agente primeiro' : ''"
              @click="setActiveTab(tab.id)"
            >
              <span :class="tab.icon" />
              {{ tab.label }}
            </button>
          </nav>
        </div>
      </div>
    </template>

    <template #body>
      <div class="px-6 py-6">
        <!-- Only render tabs when we have a valid agentId -->
        <template v-if="hasValidAgentId">
          <keep-alive>
            <component
              :is="activeTabComponent"
              ref="activeTabRef"
              :agent-id="agentId"
              @delete-success="handleDelete"
            />
          </keep-alive>
        </template>

        <!-- Show loading or error when agentId is invalid -->
        <div v-else class="flex items-center justify-center py-12">
          <p class="text-n-slate-11">Carregando agente...</p>
        </div>
      </div>
    </template>

    <DeleteDialog
      v-if="agent"
      ref="deleteAgentDialog"
      :entity="agent"
      type="Agents"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
  </PageLayout>
</template>
